/**
 * publishApi — NA-190 publish & export workflow client.
 *
 * Lifecycle endpoints (publish / unlock / publish-update) own every
 * workflowState transition; the editors never write lifecycle state through
 * saves. Deploys ride the existing account-partitioned orchestration in
 * courseDeployApi; SCORM export downloads the account context's last-deployed
 * package from the server (never a live rebuild), so exports during
 * Published — editing reflect the last published version by construction.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ScormExportHistory, ScormPackageConfig, ScormVersion } from '@course-authoring/shared-types';
import { fetchWithAuthRetry } from './apiUtils';
import type { ClientAccount, ScormExportContext } from './accountAssignmentApi';
import type { DeployAccountInput } from './courseDeployApi';
import { buildScormZipBlob, type BuildScormZipInput } from './packageBundler';
import { formatCourseHumanId, formatLessonHumanId } from '../utils/humanIds';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** The reserved base (Emtrain) deploy scope — the NULL account context. */
export const BASE_ACCOUNT_SLUG = 'emtrain';

export type { PublishLifecycleState, PublishState } from '@course-authoring/shared-types';
import type { PublishState } from '@course-authoring/shared-types';

export type PublishErrorCode =
  | 'ALREADY_PUBLISHED'
  | 'MISSING_LESSON_COMPLETE'
  | 'LESSONS_NOT_PUBLISHED'
  | 'NOT_PUBLISHED'
  | 'ALREADY_EDITING'
  | 'NOT_EDITING'
  | 'EDITS_ALREADY_SAVED'
  | 'LOCKED_BY_EMTRAIN_EDITING'
  | 'CONTENT_LOCKED'
  | 'NOT_DEPLOYED'
  | 'MISSING_ACCOUNT_CONTENT_ID'
  | 'UNKNOWN';

export class PublishApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: PublishErrorCode,
    /** Encoded lesson ids from MISSING_LESSON_COMPLETE / LESSONS_NOT_PUBLISHED responses. */
    public readonly lessonIds: string[] = [],
  ) {
    super(message);
    this.name = 'PublishApiError';
  }
}

export type PublishableContentKind = 'course' | 'lesson';

const contentPath = (kind: PublishableContentKind, id: string): string =>
  kind === 'course' ? `${API_BASE_URL}/courses/${id}` : `${API_BASE_URL}/lessons/${id}`;

async function postLifecycle<T>(url: string, body?: Record<string, unknown>): Promise<T> {
  const response = await fetchWithAuthRetry(url, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let code: PublishErrorCode = 'UNKNOWN';
    let lessonIds: string[] = [];
    try {
      const parsed = (await response.json()) as {
        message?: unknown;
        code?: unknown;
        missingLessonIds?: unknown;
        unpublishedLessonIds?: unknown;
      };
      if (typeof parsed.message === 'string') message = parsed.message;
      if (typeof parsed.code === 'string') code = parsed.code as PublishErrorCode;
      const ids = parsed.missingLessonIds ?? parsed.unpublishedLessonIds;
      if (Array.isArray(ids)) lessonIds = ids.filter((id): id is string => typeof id === 'string');
    } catch {
      // Non-JSON error body; keep the status-based message.
    }
    throw new PublishApiError(message, response.status, code, lessonIds);
  }
  return (await response.json()) as T;
}

export interface PublishResponse {
  publishedAt: string;
  versionId?: string;
  versionNumber?: number;
}

export interface LifecycleResponse {
  publishState: PublishState;
  publishedAt?: string;
}

// referenced-lesson-packages: course publish references each contained
// lesson's last-published package — never cascades or releases lesson edits —
// so there is no releaseLessons consent contract. A never-published contained
// lesson 409s with LESSONS_NOT_PUBLISHED instead.
export const publishContent = (
  kind: PublishableContentKind,
  id: string,
): Promise<PublishResponse> =>
  postLifecycle<PublishResponse>(`${contentPath(kind, id)}/publish`, {});

export const unlockContent = (
  kind: PublishableContentKind,
  id: string,
  accountId?: string | number | null,
): Promise<LifecycleResponse> =>
  postLifecycle<LifecycleResponse>(`${contentPath(kind, id)}/unlock`, accountId != null ? { accountId } : {});

export const publishUpdateContent = (
  kind: PublishableContentKind,
  id: string,
  options?: { accountId?: string | number | null },
): Promise<LifecycleResponse> =>
  postLifecycle<LifecycleResponse>(`${contentPath(kind, id)}/publish-update`, {
    ...(options?.accountId != null ? { accountId: options.accountId } : {}),
  });

// NA-296: backs an untouched editing session out of Published — editing
// straight to Published — locked, with no version snapshot and no deploy.
// 409 NOT_EDITING if the context isn't currently editing; 409
// EDITS_ALREADY_SAVED once any edit has been saved — Publish Update is the
// only way back at that point.
export const cancelEditContent = (
  kind: PublishableContentKind,
  id: string,
  accountId?: string | number | null,
): Promise<LifecycleResponse> =>
  postLifecycle<LifecycleResponse>(`${contentPath(kind, id)}/cancel-edit`, accountId != null ? { accountId } : {});

// ─── Deploy target assembly ───────────────────────────────────────────────────

/**
 * Builds the automatic deploy target set for a publish or Emtrain publish
 * update: the base (Emtrain) scope plus every actively assigned account from
 * the scorm-context prefill, partitioned downstream by hasCustomizations.
 * A client-context publish update passes `onlyAccountId` to scope the set to
 * that single account.
 */
export function buildPublishDeployTargets(
  context: ScormExportContext,
  options?: { enforceSequence?: boolean; onlyAccountId?: number },
): DeployAccountInput[] {
  const enforceSequence = options?.enforceSequence === true;
  const shared = {
    host: context.host,
    contentId: context.contentId,
    contentType: context.contentType,
    enforceSequence,
    ...(context.apiBaseUrl ? { apiBaseUrl: context.apiBaseUrl } : {}),
    ...(context.authorAiCourseId ? { authorAiCourseId: context.authorAiCourseId } : {}),
  } as const;

  const accountTargets: DeployAccountInput[] = [];
  const missingContentIdAccounts: string[] = [];
  for (const account of context.accounts) {
    const externalAccountId = Number(account.externalId);
    const subdomain = account.subdomain?.trim() ?? '';
    if (!Number.isInteger(externalAccountId) || externalAccountId <= 0 || !subdomain) continue;
    if (options?.onlyAccountId !== undefined && externalAccountId !== options.onlyAccountId) continue;
    // NA-361: course account targets bake the account's OWN shell id from the
    // scorm context — never `context.contentId` (that IS the Standard shell,
    // reserved for the base target). A missing per-account id fails the deploy
    // closed rather than falling back: a blocked deploy beats silently wrong
    // progress tracking. Standalone lessons stay single-scope on the shared id.
    let contentId = context.contentId;
    if (context.contentType === 'programs') {
      if (!account.contentId) {
        missingContentIdAccounts.push(account.name || subdomain);
        continue;
      }
      contentId = account.contentId;
    }
    accountTargets.push({
      externalAccountId,
      accountSlug: subdomain,
      hasCustomizations: account.hasCustomizations === true,
      scormRuntimeConfig: { mode: 'emtrain', subdomain, ...shared, contentId },
    });
  }

  if (missingContentIdAccounts.length > 0) {
    throw new PublishApiError(
      `No client program id was resolved for account(s): ${missingContentIdAccounts.join(', ')}. `
        + 'Refresh and retry — deploying these accounts with the shared Standard id would record '
        + 'learner progress against the wrong program.',
      400,
      'MISSING_ACCOUNT_CONTENT_ID',
    );
  }

  if (options?.onlyAccountId !== undefined) return accountTargets;

  const baseTarget: DeployAccountInput = {
    base: true,
    accountSlug: BASE_ACCOUNT_SLUG,
    scormRuntimeConfig: { mode: 'emtrain', subdomain: BASE_ACCOUNT_SLUG, ...shared },
  };
  return [baseTarget, ...accountTargets];
}

// ─── SCORM export (thin RXD wrapper) ─────────────────────────────────────────
//
// The SCORM .zip is NOT the hosted package: it is the thin RXD wrapper
// (imsmanifest.xml + SCORM SCO files) whose baked configuration points at the
// account scope's last-DEPLOYED package URL — the pre-workflow "Download
// SCORM" artifact, with every form field now auto-set (NA-211). Because the
// wrapper targets the deployed package, exporting during Published — editing
// yields the last published version by construction.

export interface ExportPackageTestingInfo {
  accountSlug: string;
  coursePackageUrl: string;
}

/** Client package-info is a read-only availability check with no wrapper URL. */
export interface ExportPackageAvailability {
  accountSlug: string;
  deployed: true;
}

export type ExportPackageInfo = ExportPackageTestingInfo | ExportPackageAvailability;

export interface ClientScormExportActionResponse extends ExportPackageTestingInfo {
  account: {
    accountId: number;
    name: string;
    subdomain: string;
    apiKey: string;
    integrationKey: string;
    scormVersion: string;
  };
  exportHistory: ScormExportHistory;
}
export type ExportPackagePurpose = 'standalone' | 'reference';

/** Fetches the hosted package URL for the export scope; 404 NOT_DEPLOYED when the scope was never deployed. */
export async function fetchExportPackageInfo(
  packageId: string,
  accountSlug?: string,
  purpose?: ExportPackagePurpose,
): Promise<ExportPackageInfo> {
  const params = new URLSearchParams();
  if (accountSlug) params.set('accountSlug', accountSlug);
  if (purpose) params.set('purpose', purpose);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${packageId}/export-package-info${query}`);
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    let code: PublishErrorCode = 'UNKNOWN';
    try {
      const parsed = (await response.json()) as { message?: unknown; code?: unknown };
      if (typeof parsed.message === 'string') message = parsed.message;
      if (typeof parsed.code === 'string') code = parsed.code as PublishErrorCode;
    } catch {
      // Non-JSON error body.
    }
    throw new PublishApiError(message, response.status, code);
  }
  return (await response.json()) as ExportPackageInfo;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function parseClientScormExportActionResponse(value: unknown): ClientScormExportActionResponse | null {
  if (!isRecord(value) || !isRecord(value.account) || !isRecord(value.exportHistory)) return null;
  const { account, exportHistory } = value;
  const accountId = account.accountId;
  const firstExportedAt = exportHistory.firstExportedAt;
  if (
    !nonEmptyString(value.accountSlug)
    || !nonEmptyString(value.coursePackageUrl)
    || !Number.isInteger(accountId)
    || (accountId as number) <= 0
    || !nonEmptyString(account.name)
    || !nonEmptyString(account.subdomain)
    || value.accountSlug !== account.subdomain
    || !nonEmptyString(account.apiKey)
    || !nonEmptyString(account.integrationKey)
    || !nonEmptyString(account.scormVersion)
    || exportHistory.hasBeenExported !== true
    || !nonEmptyString(firstExportedAt)
    || !Number.isFinite(Date.parse(firstExportedAt))
  ) {
    return null;
  }

  return {
    accountSlug: value.accountSlug,
    coursePackageUrl: value.coursePackageUrl,
    account: {
      accountId: accountId as number,
      name: account.name,
      subdomain: account.subdomain,
      apiKey: account.apiKey,
      integrationKey: account.integrationKey,
      scormVersion: account.scormVersion,
    },
    exportHistory: { hasBeenExported: true, firstExportedAt },
  };
}

async function fetchClientScormExportInputs(
  packageId: string,
  context: ScormExportContext,
  account: ClientAccount,
): Promise<ClientScormExportActionResponse> {
  const rawExternalAccountId = account.externalId ?? account.id;
  const externalAccountId = Number(rawExternalAccountId);
  if (!Number.isInteger(externalAccountId) || externalAccountId <= 0) {
    throw new PublishApiError('This client account has no valid external account id.', 400, 'UNKNOWN');
  }

  const collection = context.contentType === 'programs' ? 'courses' : 'lessons';
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/${collection}/${encodeURIComponent(packageId)}/scorm-export`,
    { method: 'POST', body: JSON.stringify({ externalAccountId }) },
  );
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    let code: PublishErrorCode = 'UNKNOWN';
    try {
      const parsed = (await response.json()) as { message?: unknown; code?: unknown };
      if (typeof parsed.message === 'string') message = parsed.message;
      if (typeof parsed.code === 'string') code = parsed.code as PublishErrorCode;
    } catch {
      // Non-JSON error body.
    }
    throw new PublishApiError(message, response.status, code);
  }

  const result = parseClientScormExportActionResponse(await response.json());
  if (!result) {
    throw new PublishApiError('The export action returned invalid wrapper inputs or history.', response.status, 'UNKNOWN');
  }
  if (result.account.accountId !== externalAccountId) {
    throw new PublishApiError('The export action returned a different client account.', response.status, 'UNKNOWN');
  }
  return result;
}

export interface PackageLiveStatus {
  deployed: boolean;
  /**
   * True only on a GLOBAL propagation guarantee: the deploy's CloudFront
   * invalidation completed, or a full edge TTL elapsed since the deploy —
   * never from a single sampled edge response.
   */
  live: boolean;
  /** Why not live yet: 'invalidation-in-progress' | 'ttl-window' | 'edge-stale' | 'edge-<status>' | 'edge-unreachable'. */
  reason?: string;
  checkedAt: string;
  /**
   * Invalidation provenance: 'completed' = CloudFront's global purge finished;
   * 'in-progress' = still purging; 'skipped' | 'failed' | 'unknown' = nothing
   * to trust, so the server falls back to the edge-TTL argument (reason
   * 'ttl-window' until it elapses) — the badge surfaces that degraded mode.
   */
  invalidation?: { state?: string };
  /** S3 object time of the deployed data/course.json — compared with lastPublishedAt to catch published-but-never-deployed. */
  origin?: { lastModified?: string | null };
  /** The hosted content revision (stamp/meta) — preferred over origin.lastModified for the publish-gap check. */
  contentPublishedAt?: string | null;
  edge?: { lastModified?: string | null; ageSeconds?: number; xCache?: string | null };
  /** Identity of the deploy the verdict verified against (deploy-stamp.json). */
  deployId?: string;
  /**
   * Course-pointer composite: per-referenced-lesson liveness for this scope.
   * Present only for pointer courses; overall `live` requires every entry
   * live (reason 'lessons-propagating' while any is pending).
   */
  lessons?: Array<{
    id: string;
    live: boolean;
    /** e.g. 'ttl-window' | 'not-deployed' | 'stale-revision' | 'status-unavailable'. */
    reason?: string;
    invalidation?: { state?: string };
    /** Best-known time of the hosted lesson deploy (stamp → meta → object time). */
    deployedAt?: string | null;
  }>;
}

/**
 * Served-truth CDN check for the "Live on CDN" badge: the server compares the
 * S3 origin bytes of the scope's `data/course.json` with what CloudFront is
 * actually serving. 404 (never deployed) resolves to `{deployed:false}` so the
 * badge can hide instead of erroring.
 */
export async function fetchPackageLiveStatus(
  packageId: string,
  accountSlug?: string,
): Promise<PackageLiveStatus> {
  const query = accountSlug ? `?accountSlug=${encodeURIComponent(accountSlug)}` : '';
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${packageId}/live-status${query}`);
  if (response.status === 404) {
    return { deployed: false, live: false, checkedAt: new Date().toISOString() };
  }
  if (!response.ok) {
    throw new PublishApiError(`Live-status check failed (${response.status})`, response.status, 'UNKNOWN');
  }
  return (await response.json()) as PackageLiveStatus;
}

/**
 * Context-aware lesson lifecycle read (base when `accountId` is null), used by
 * the course-pointer heal step to decide whether a lesson's live rows are
 * provably its published content (`state === 'locked'`) before building a
 * package from them.
 */
export async function fetchLessonPublishState(
  lessonId: string,
  accountId: number | null = null,
): Promise<PublishState> {
  const query = accountId !== null ? `?accountId=${accountId}` : '';
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}${query}`);
  if (!response.ok) {
    throw new PublishApiError(
      `Failed to load lesson publish state (${response.status})`,
      response.status,
      'UNKNOWN',
    );
  }
  const data = (await response.json()) as { publishState?: PublishState };
  if (!data.publishState || typeof data.publishState.state !== 'string') {
    throw new PublishApiError('Lesson response carried no publish state.', response.status, 'UNKNOWN');
  }
  return data.publishState;
}

/**
 * The Emtrain testing export has no client account, so no real credentials
 * exist to bake into the wrapper. Documented placeholders keep the wrapper
 * structurally valid, and `testMode` rides along on the contentUrl so the
 * player skips the verify/assignment chain and plays untracked — the
 * dialog's promise ("learner progress will not be tracked in any client
 * account"). Without the flag the launch would die on verifyScorm 401,
 * since the placeholders can never authenticate.
 */
const EMTRAIN_TEST_CREDENTIAL = 'emtrain-testing';

const normalizeScormVersion = (raw: string | undefined): ScormVersion => {
  const value = (raw ?? '').toLowerCase();
  if (value.includes('4th')) return '2004_4th';
  if (value.includes('2004')) return '2004_3rd';
  return '1.2';
};

export interface ScormWrapperParams {
  packageId: string;
  title: string;
  context: ScormExportContext;
  /** The active client account, or null for the Emtrain testing scope. */
  account: ClientAccount | null;
  coursePackageUrl: string;
  enforceSequence?: boolean;
}

/**
 * Assembles the auto-set inputs the old Download-SCORM form collected by hand.
 *
 * Deliberately carries NO hoot program/lesson id: the wrapper is a thin
 * launcher (package URL + credentials + testMode/enforceSequence), and the
 * scope's content id lives solely in the hosted package's baked
 * `window.__SCORM_RUNTIME__` (per-account shell ids under NA-361). That is
 * what keeps already-uploaded LMS zips valid when the hosted side changes.
 */
export function buildScormWrapperInput(params: ScormWrapperParams): BuildScormZipInput {
  const { account } = params;
  const scorm: ScormPackageConfig = {
    coursePackageUrl: params.coursePackageUrl,
    apiKey: account?.apiKey?.trim() || EMTRAIN_TEST_CREDENTIAL,
    integrationKey: account?.integrationKey?.trim() || EMTRAIN_TEST_CREDENTIAL,
    enforceSequence: params.enforceSequence === true,
    ...(account ? {} : { testMode: true }),
  };
  return {
    courseId: params.packageId,
    courseTitle: params.title,
    scormVersion: normalizeScormVersion(account?.scormVersion),
    scorm,
  };
}

/**
 * Names the export zip on the legacy LMS pattern (`genClientFilename` in
 * hoot-server): `{Title}_{id}_SCORM_{version}_{YYYYMMDD}.zip`, e.g.
 * `Antitrust_Compliance_and_Competition_3_C10_SCORM_2004_3rd_20250627.zip`.
 *
 * The id slot is the AuthorAI human id the authoring UI shows ("C{n}"/"L{n}"),
 * NOT `ScormExportContext.contentId` — that is hoot's internal shell id, which
 * appears nowhere an author can see, so it could not be matched back to the
 * content it packaged.
 */
export function buildScormExportFilename(params: {
  title: string;
  /** Stands in for the title when it sanitizes to nothing. */
  packageId: string;
  context: ScormExportContext;
  /**
   * `authorAICourses.id` / `authorAILessons.id` — the number behind the
   * "C{n}"/"L{n}" badge. Undefined only against an API too old to send it, and
   * the name then keeps hoot's id rather than losing its id slot entirely.
   */
  numericId: number | undefined;
  scormVersion: ScormVersion;
  downloadedAt?: Date;
}): string {
  const cleanTitle =
    params.title.replace(/\W+/g, '_').replace(/^_+|_+$/g, '') || params.packageId;
  const humanId = params.context.contentType === 'programs'
    ? formatCourseHumanId(params.numericId, params.context.contentId)
    : formatLessonHumanId(params.numericId, params.context.contentId);
  const date = params.downloadedAt ?? new Date();
  const stamp =
    `${date.getFullYear()}` +
    `${String(date.getMonth() + 1).padStart(2, '0')}` +
    `${String(date.getDate()).padStart(2, '0')}`;
  return `${cleanTitle}_${humanId}_SCORM_${params.scormVersion}_${stamp}.zip`;
}

export async function downloadScormExport(
  params: Omit<ScormWrapperParams, 'coursePackageUrl'> & { numericId?: number },
): Promise<void> {
  let account = params.account;
  let coursePackageUrl: string;
  if (account) {
    const action = await fetchClientScormExportInputs(params.packageId, params.context, account);
    account = {
      id: String(action.account.accountId),
      externalId: String(action.account.accountId),
      name: action.account.name,
      subdomain: action.account.subdomain,
      apiKey: action.account.apiKey,
      integrationKey: action.account.integrationKey,
      scormVersion: action.account.scormVersion,
    };
    coursePackageUrl = action.coursePackageUrl;
  } else {
    const info = await fetchExportPackageInfo(params.packageId);
    if (!('coursePackageUrl' in info) || !nonEmptyString(info.coursePackageUrl)) {
      throw new PublishApiError('The Emtrain testing package has no wrapper URL.', 502, 'UNKNOWN');
    }
    coursePackageUrl = info.coursePackageUrl;
  }
  const input = buildScormWrapperInput({ ...params, account, coursePackageUrl });
  const blob = await buildScormZipBlob(input);

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = buildScormExportFilename({
    title: params.title,
    packageId: params.packageId,
    context: params.context,
    numericId: params.numericId,
    scormVersion: input.scormVersion,
  });
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/** Invalidate every course/lesson read so lists, editors, and badges refetch. */
function useInvalidateContent() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['courseRecords'] });
    void queryClient.invalidateQueries({ queryKey: ['lessons'] });
  };
}

export function usePublishContent(kind: PublishableContentKind) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (id: string) => publishContent(kind, id),
    onSuccess: invalidate,
  });
}

export function useUnlockContent(kind: PublishableContentKind) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (params: { id: string; accountId?: string | number | null }) =>
      unlockContent(kind, params.id, params.accountId),
    onSuccess: invalidate,
  });
}

export function usePublishUpdateContent(kind: PublishableContentKind) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (params: { id: string; accountId?: string | number | null }) =>
      publishUpdateContent(kind, params.id, params),
    onSuccess: invalidate,
  });
}
